import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  FileText, DollarSign, TrendingUp, TrendingDown, Calendar,
  Download, PieChart, BarChart3, Calculator, Receipt, Clock,
  CheckCircle, AlertCircle, ChevronRight, Filter
} from 'lucide-react';
import { IconTile, TINT } from '../components/ui/IconTile';
import { generateTaxReportPDF } from '../utils/PDFGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// IRS Schedule C Categories
const SCHEDULE_C_CATEGORIES = {
  'advertising': 'Advertising',
  'car_truck': 'Car and truck expenses',
  'commissions': 'Commissions and fees',
  'contract_labor': 'Contract labor',
  'depletion': 'Depletion',
  'depreciation': 'Depreciation',
  'employee_benefits': 'Employee benefit programs',
  'insurance': 'Insurance (other than health)',
  'interest_mortgage': 'Interest: Mortgage',
  'interest_other': 'Interest: Other',
  'legal_professional': 'Legal and professional services',
  'office_expense': 'Office expense',
  'pension_plans': 'Pension and profit-sharing plans',
  'rent_equipment': 'Rent: Vehicles, machinery, equipment',
  'rent_property': 'Rent: Other business property',
  'repairs_maintenance': 'Repairs and maintenance',
  'supplies': 'Supplies',
  'taxes_licenses': 'Taxes and licenses',
  'travel': 'Travel',
  'meals': 'Meals (50% deductible)',
  'utilities': 'Utilities',
  'wages': 'Wages',
  'other': 'Other expenses'
};

// Map app categories to Schedule C
const CATEGORY_TO_SCHEDULE_C = {
  '🍽️ Meals & Entertainment': 'meals',
  '✈️ Travel & Transportation': 'travel',
  '📦 Office Supplies': 'supplies',
  '🏢 Rent & Utilities': 'rent_property',
  '💼 Professional Services': 'legal_professional',
  '🚗 Vehicle & Fuel': 'car_truck',
  '📱 Phone & Internet': 'utilities',
  '🛠️ Repairs & Maintenance': 'repairs_maintenance',
  '📚 Training & Education': 'other',
  '💰 Other Business Expenses': 'other'
};

const ReportsTaxes = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('summary');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Jan 1st
    end: new Date().toISOString().split('T')[0] // Today
  });
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [mileage, setMileage] = useState([]);
  
  // Calculated states
  const [taxSummary, setTaxSummary] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [quarterlyEstimates, setQuarterlyEstimates] = useState(null);

  useEffect(() => {
    loadAllData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dateRange]);

  const loadAllData = async () => {
    if (!user?.id) return;
    setLoading(true);
    
    try {
      // Load expenses
      const expensesRes = await fetch(`${API_URL}/api/expenses/list/${user.id}`);
      const expensesData = await expensesRes.json();
      if (expensesData.success) {
        setExpenses(expensesData.expenses || []);
      }
      
      // Load invoices from localStorage (or API if available)
      const invoicesData = JSON.parse(localStorage.getItem('maidly_invoices') || '[]');
      setInvoices(invoicesData);
      
      // Load mileage from localStorage
      const mileageData = JSON.parse(localStorage.getItem('maidly_mileageEntries') || '[]');
      setMileage(mileageData);
      
      // Calculate reports
      calculateTaxSummary(expensesData.expenses || [], mileageData);
      calculateProfitLoss(invoicesData, expensesData.expenses || [], mileageData);
      calculateQuarterlyEstimates(invoicesData, expensesData.expenses || [], mileageData);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const filterByDateRange = (items, dateField = 'date') => {
    return items.filter(item => {
      const itemDate = item[dateField];
      if (!itemDate) return false;
      return itemDate >= dateRange.start && itemDate <= dateRange.end;
    });
  };

  const calculateTaxSummary = (expensesData, mileageData) => {
    const filteredExpenses = filterByDateRange(expensesData);
    const filteredMileage = filterByDateRange(mileageData);
    
    // Group by Schedule C category
    const byCategory = {};
    
    filteredExpenses.forEach(exp => {
      const scheduleC = CATEGORY_TO_SCHEDULE_C[exp.category] || 'other';
      if (!byCategory[scheduleC]) {
        byCategory[scheduleC] = { total: 0, deductible: 0, items: [] };
      }
      
      const amount = exp.amount || 0;
      const deductible = exp.tax_deductible !== false ? amount : 0;
      
      // Meals are 50% deductible
      const actualDeductible = scheduleC === 'meals' ? deductible * 0.5 : deductible;
      
      byCategory[scheduleC].total += amount;
      byCategory[scheduleC].deductible += actualDeductible;
      byCategory[scheduleC].items.push(exp);
    });
    
    // Add mileage deduction
    const businessMileage = filteredMileage.filter(m => m.type === 'business');
    const totalMiles = businessMileage.reduce((sum, m) => sum + (m.miles || 0), 0);
    const mileageRate = 0.67; // 2024 IRS rate
    const mileageDeduction = totalMiles * mileageRate;
    
    if (totalMiles > 0) {
      byCategory['car_truck'] = byCategory['car_truck'] || { total: 0, deductible: 0, items: [] };
      byCategory['car_truck'].deductible += mileageDeduction;
      byCategory['car_truck'].total += mileageDeduction;
    }
    
    const totalExpenses = Object.values(byCategory).reduce((sum, cat) => sum + cat.total, 0);
    const totalDeductible = Object.values(byCategory).reduce((sum, cat) => sum + cat.deductible, 0);
    const nonDeductible = totalExpenses - totalDeductible;
    
    setTaxSummary({
      byCategory,
      totalExpenses,
      totalDeductible,
      nonDeductible,
      mileageDeduction,
      totalMiles
    });
  };

  const calculateProfitLoss = (invoicesData, expensesData, mileageData) => {
    const filteredInvoices = filterByDateRange(invoicesData, 'date');
    const filteredExpenses = filterByDateRange(expensesData);
    const filteredMileage = filterByDateRange(mileageData);
    
    // Revenue from paid invoices
    const paidInvoices = filteredInvoices.filter(inv => inv.status === 'paid');
    const totalRevenue = paidInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    
    // Pending revenue
    const pendingInvoices = filteredInvoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled');
    const pendingRevenue = pendingInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    
    // Total expenses
    const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
    
    // Mileage deduction
    const businessMileage = filteredMileage.filter(m => m.type === 'business');
    const mileageDeduction = businessMileage.reduce((sum, m) => sum + (m.miles || 0), 0) * 0.67;
    
    const netProfit = totalRevenue - totalExpenses - mileageDeduction;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    
    // Monthly breakdown
    const monthlyData = {};
    
    filteredInvoices.forEach(inv => {
      if (inv.status === 'paid') {
        const month = inv.date?.substring(0, 7) || 'Unknown';
        if (!monthlyData[month]) {
          monthlyData[month] = { revenue: 0, expenses: 0 };
        }
        monthlyData[month].revenue += inv.total || 0;
      }
    });
    
    filteredExpenses.forEach(exp => {
      const month = exp.date?.substring(0, 7) || 'Unknown';
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0 };
      }
      monthlyData[month].expenses += exp.amount || 0;
    });
    
    setProfitLoss({
      totalRevenue,
      pendingRevenue,
      totalExpenses,
      mileageDeduction,
      netProfit,
      profitMargin,
      monthlyData,
      invoiceCount: paidInvoices.length,
      pendingCount: pendingInvoices.length
    });
  };

  const calculateQuarterlyEstimates = (invoicesData, expensesData, mileageData) => {
    const currentYear = new Date().getFullYear();
    const quarters = [
      { name: 'Q1', start: `${currentYear}-01-01`, end: `${currentYear}-03-31`, due: `${currentYear}-04-15` },
      { name: 'Q2', start: `${currentYear}-04-01`, end: `${currentYear}-06-30`, due: `${currentYear}-06-15` },
      { name: 'Q3', start: `${currentYear}-07-01`, end: `${currentYear}-09-30`, due: `${currentYear}-09-15` },
      { name: 'Q4', start: `${currentYear}-10-01`, end: `${currentYear}-12-31`, due: `${currentYear + 1}-01-15` }
    ];
    
    const quarterlyData = quarters.map(q => {
      const qInvoices = invoicesData.filter(inv => 
        inv.date >= q.start && inv.date <= q.end && inv.status === 'paid'
      );
      const qExpenses = expensesData.filter(exp => 
        exp.date >= q.start && exp.date <= q.end
      );
      const qMileage = mileageData.filter(m => 
        m.date >= q.start && m.date <= q.end && m.type === 'business'
      );
      
      const revenue = qInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
      const expenses = qExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
      const mileageDeduction = qMileage.reduce((sum, m) => sum + (m.miles || 0), 0) * 0.67;
      
      const netIncome = revenue - expenses - mileageDeduction;
      
      // Estimate self-employment tax (15.3%) and income tax (assume 22% bracket)
      const selfEmploymentTax = Math.max(0, netIncome * 0.9235 * 0.153);
      const estimatedIncomeTax = Math.max(0, netIncome * 0.22);
      const totalEstimatedTax = selfEmploymentTax + estimatedIncomeTax;
      
      const isPast = new Date(q.end) < new Date();
      const isDue = new Date(q.due) < new Date();
      
      return {
        ...q,
        revenue,
        expenses,
        mileageDeduction,
        netIncome,
        selfEmploymentTax,
        estimatedIncomeTax,
        totalEstimatedTax,
        isPast,
        isDue
      };
    });
    
    const totalEstimatedTax = quarterlyData.reduce((sum, q) => sum + q.totalEstimatedTax, 0);
    const annualNetIncome = quarterlyData.reduce((sum, q) => sum + q.netIncome, 0);
    
    setQuarterlyEstimates({
      quarters: quarterlyData,
      totalEstimatedTax,
      annualNetIncome
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const downloadTaxReport = () => {
    if (!taxSummary) {
      toast.error('No data to export yet');
      return;
    }

    generateTaxReportPDF({
      taxSummary,
      profitLoss,
      quarterlyEstimates,
      scheduleCLabels: SCHEDULE_C_CATEGORIES,
      dateRange,
    });

    toast.success('Tax report downloaded!');
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.5rem', color: '#6b7280' }}>Loading reports...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 2rem 0' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#1a1a1a', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <IconTile icon={PieChart} tint={TINT.reports} size={36} />
            Reports &amp; Taxes
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Tax summaries, profit/loss statements, and quarterly estimates
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Date Range Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f3f4f6', padding: '0.5rem', borderRadius: '8px' }}>
            <Calendar size={16} color="#6b7280" />
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              style={{ border: 'none', background: 'transparent', fontSize: '0.875rem' }}
            />
            <span style={{ color: '#6b7280' }}>to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              style={{ border: 'none', background: 'transparent', fontSize: '0.875rem' }}
            />
          </div>
          
          <button
            onClick={downloadTaxReport}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1.25rem',
              background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            <Download size={18} />
            Export Report
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: '0.5rem'
      }}>
        {[
          { id: 'summary', label: 'Tax Summary', icon: FileText },
          { id: 'pnl', label: 'Profit & Loss', icon: TrendingUp },
          { id: 'quarterly', label: 'Quarterly Estimates', icon: Calculator }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                background: activeTab === tab.id ? '#0073ea' : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#6b7280',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tax Summary Tab */}
      {activeTab === 'summary' && taxSummary && (
        <div>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
              borderRadius: '16px',
              border: '1px solid #86efac'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <CheckCircle size={20} color="#166534" />
                <span style={{ fontSize: '0.875rem', color: '#166534', fontWeight: 500 }}>Total Deductible</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#166534' }}>
                {formatCurrency(taxSummary.totalDeductible)}
              </div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #e0f2ff 0%, #bae6fd 100%)',
              borderRadius: '16px',
              border: '1px solid #7dd3fc'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Receipt size={20} color="#0369a1" />
                <span style={{ fontSize: '0.875rem', color: '#0369a1', fontWeight: 500 }}>Mileage Deduction</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#0369a1' }}>
                {formatCurrency(taxSummary.mileageDeduction)}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#0369a1', marginTop: '0.25rem' }}>
                {taxSummary.totalMiles.toFixed(0)} business miles
              </div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '16px',
              border: '1px solid #fcd34d'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <AlertCircle size={20} color="#92400e" />
                <span style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: 500 }}>Non-Deductible</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#92400e' }}>
                {formatCurrency(taxSummary.nonDeductible)}
              </div>
            </div>
          </div>

          {/* Schedule C Breakdown */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <PieChart size={18} />
                Schedule C Categories
              </h3>
            </div>
            <div style={{ padding: '1rem' }}>
              {Object.entries(taxSummary.byCategory)
                .sort((a, b) => b[1].deductible - a[1].deductible)
                .map(([key, data]) => (
                  <div key={key} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 0',
                    borderBottom: '1px solid #f3f4f6'
                  }}>
                    <span style={{ color: '#374151' }}>{SCHEDULE_C_CATEGORIES[key] || key}</span>
                    <span style={{ fontWeight: 600, color: '#166534' }}>{formatCurrency(data.deductible)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Profit & Loss Tab */}
      {activeTab === 'pnl' && profitLoss && (
        <div>
          {/* P&L Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
              borderRadius: '16px',
              border: '1px solid #86efac'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <TrendingUp size={20} color="#166534" />
                <span style={{ fontSize: '0.875rem', color: '#166534', fontWeight: 500 }}>Total Revenue</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#166534' }}>
                {formatCurrency(profitLoss.totalRevenue)}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#166534', marginTop: '0.25rem' }}>
                {profitLoss.invoiceCount} paid invoices
              </div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '16px',
              border: '1px solid #fcd34d'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Clock size={20} color="#92400e" />
                <span style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: 500 }}>Pending Revenue</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#92400e' }}>
                {formatCurrency(profitLoss.pendingRevenue)}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.25rem' }}>
                {profitLoss.pendingCount} pending invoices
              </div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              borderRadius: '16px',
              border: '1px solid #f87171'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <TrendingDown size={20} color="#991b1b" />
                <span style={{ fontSize: '0.875rem', color: '#991b1b', fontWeight: 500 }}>Total Expenses</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#991b1b' }}>
                {formatCurrency(profitLoss.totalExpenses + profitLoss.mileageDeduction)}
              </div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: profitLoss.netProfit >= 0 
                ? 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)'
                : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
              borderRadius: '16px',
              color: '#fff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <DollarSign size={20} />
                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Net Profit</span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                {formatCurrency(profitLoss.netProfit)}
              </div>
              <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', opacity: 0.9 }}>
                {profitLoss.profitMargin.toFixed(1)}% margin
              </div>
            </div>
          </div>

          {/* Monthly Breakdown */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BarChart3 size={18} />
                Monthly Breakdown
              </h3>
            </div>
            <div style={{ padding: '1rem' }}>
              {Object.entries(profitLoss.monthlyData)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([month, data]) => {
                  const profit = data.revenue - data.expenses;
                  return (
                    <div key={month} style={{
                      display: 'grid',
                      gridTemplateColumns: '100px 1fr 1fr 1fr',
                      gap: '1rem',
                      padding: '0.75rem 0',
                      borderBottom: '1px solid #f3f4f6',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontWeight: 500, color: '#374151' }}>{month}</span>
                      <span style={{ color: '#166534' }}>+{formatCurrency(data.revenue)}</span>
                      <span style={{ color: '#991b1b' }}>-{formatCurrency(data.expenses)}</span>
                      <span style={{ 
                        fontWeight: 600, 
                        color: profit >= 0 ? '#0073ea' : '#dc2626' 
                      }}>
                        {formatCurrency(profit)}
                      </span>
                    </div>
                  );
                })}
              {Object.keys(profitLoss.monthlyData).length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  No data for selected period
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quarterly Estimates Tab */}
      {activeTab === 'quarterly' && quarterlyEstimates && (
        <div>
          {/* Annual Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
              borderRadius: '16px',
              color: '#fff'
            }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', opacity: 0.9 }}>
                Estimated Annual Net Income
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700 }}>
                {formatCurrency(quarterlyEstimates.annualNetIncome)}
              </div>
            </div>

            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
              borderRadius: '16px',
              color: '#fff'
            }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', opacity: 0.9 }}>
                Estimated Annual Tax
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 700 }}>
                {formatCurrency(quarterlyEstimates.totalEstimatedTax)}
              </div>
              <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', opacity: 0.8 }}>
                Self-employment + Income tax
              </div>
            </div>
          </div>

          {/* Quarterly Breakdown */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calculator size={18} />
                Quarterly Estimated Payments
              </h3>
            </div>
            <div style={{ padding: '1rem' }}>
              {quarterlyEstimates.quarters.map((q, index) => (
                <div key={q.name} style={{
                  padding: '1.25rem',
                  marginBottom: index < 3 ? '1rem' : 0,
                  background: q.isPast ? '#f9fafb' : '#fff',
                  borderRadius: '12px',
                  border: `1px solid ${q.isDue && !q.isPast ? '#f87171' : '#e5e7eb'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ 
                        padding: '0.5rem 1rem', 
                        background: q.isPast ? '#e5e7eb' : '#0073ea', 
                        color: q.isPast ? '#6b7280' : '#fff',
                        borderRadius: '8px',
                        fontWeight: 700
                      }}>
                        {q.name}
                      </span>
                      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                        {q.start} to {q.end}
                      </span>
                    </div>
                    <div style={{ 
                      padding: '0.375rem 0.75rem',
                      background: q.isDue ? '#fee2e2' : '#dcfce7',
                      color: q.isDue ? '#991b1b' : '#166534',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}>
                      Due: {q.due}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Revenue</div>
                      <div style={{ fontWeight: 600, color: '#166534' }}>{formatCurrency(q.revenue)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Expenses</div>
                      <div style={{ fontWeight: 600, color: '#991b1b' }}>{formatCurrency(q.expenses + q.mileageDeduction)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Net Income</div>
                      <div style={{ fontWeight: 600, color: q.netIncome >= 0 ? '#0073ea' : '#dc2626' }}>
                        {formatCurrency(q.netIncome)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Est. Tax Payment</div>
                      <div style={{ fontWeight: 700, fontSize: '1.25rem', color: '#dc2626' }}>
                        {formatCurrency(q.totalEstimatedTax)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#fef3c7',
            borderRadius: '12px',
            border: '1px solid #fcd34d',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem'
          }}>
            <AlertCircle size={20} color="#92400e" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '0.85rem', color: '#92400e' }}>
              <strong>Disclaimer:</strong> These are estimates based on a 22% income tax bracket and standard self-employment tax rates. 
              Actual tax liability may vary. Consult a tax professional for accurate calculations.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsTaxes;
